/**
 * Decorative geometric backdrop for every page inside the AppShell.
 *
 * Viewport-fixed at z-index -10: that keeps it above the page's base colour
 * (which comes from `body`) but below all in-flow content, so pages need no
 * stacking classes of their own. The sidebar stays opaque and covers it; the
 * navbar is transparent so the artwork runs through the header.
 *
 * Purely ornamental — hidden from assistive tech, never takes pointer events.
 *
 * Palette is the existing warm cream + soft green one:
 *   #f4f0e7 base · #efe9dc / #eae3d3 / #d9cbb7 tan · #e7f0e8 / #d9e8dd green
 */
export function AppBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Wide viewports: shapes live in the gutters either side of the content column. */}
      <svg
        className="hidden size-full sm:block"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <defs>
          <linearGradient id="meaning-bd-tile" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#e3ede4" />
            <stop offset="100%" stopColor="#f4f0e7" />
          </linearGradient>
          <linearGradient id="meaning-bd-tile-soft" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#efe9dc" />
            <stop offset="100%" stopColor="#f7f3ea" />
          </linearGradient>
          <radialGradient id="meaning-bd-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d9e8dd" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#d9e8dd" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Two-tone diagonal split, echoing the reference layout. */}
        <path d="M1440 0v352L0 900V536Z" fill="#efe9dc" />
        <path d="M1440 128v148L0 764V632Z" fill="#eae3d3" opacity="0.75" />

        {/* Rounded diamond, top-left gutter. */}
        <rect
          x="-96"
          y="-132"
          width="440"
          height="440"
          rx="104"
          transform="rotate(20 124 88)"
          fill="url(#meaning-bd-tile)"
          opacity="0.9"
        />
        {/* Outlined echo of the same shape. */}
        <rect
          x="-16"
          y="-52"
          width="440"
          height="440"
          rx="104"
          transform="rotate(20 204 168)"
          fill="none"
          stroke="#d9cbb7"
          strokeWidth="2"
          opacity="0.55"
        />

        {/* Chevron, right gutter. */}
        <path
          d="M1264 -32 1436 218 1264 468"
          fill="none"
          stroke="#e3ede4"
          strokeWidth="58"
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity="0.85"
        />

        {/* Rounded diamond, bottom-right gutter. */}
        <rect
          x="1176"
          y="596"
          width="336"
          height="336"
          rx="88"
          transform="rotate(-18 1344 764)"
          fill="url(#meaning-bd-tile-soft)"
        />
        <rect
          x="1128"
          y="548"
          width="336"
          height="336"
          rx="88"
          transform="rotate(-18 1296 716)"
          fill="none"
          stroke="#d9cbb7"
          strokeWidth="2"
          opacity="0.5"
        />

        {/* Small accents + ambient glow, left gutter. */}
        <rect
          x="52"
          y="452"
          width="128"
          height="128"
          rx="36"
          transform="rotate(38 116 516)"
          fill="#e3ede4"
          opacity="0.9"
        />
        <circle cx="96" cy="836" r="300" fill="url(#meaning-bd-glow)" />
      </svg>

      {/* Narrow viewports: portrait composition so the shapes stay at the edges. */}
      <svg
        className="size-full sm:hidden"
        viewBox="0 0 390 844"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <defs>
          <linearGradient id="meaning-bd-tile-sm" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#e3ede4" />
            <stop offset="100%" stopColor="#f4f0e7" />
          </linearGradient>
          <radialGradient id="meaning-bd-glow-sm" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d9e8dd" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#d9e8dd" stopOpacity="0" />
          </radialGradient>
        </defs>

        <path d="M390 96v268L0 656V388Z" fill="#efe9dc" />
        <path d="M390 424v84L0 780v-84Z" fill="#eae3d3" opacity="0.7" />

        <rect
          x="248"
          y="-104"
          width="260"
          height="260"
          rx="66"
          transform="rotate(22 378 26)"
          fill="url(#meaning-bd-tile-sm)"
          opacity="0.9"
        />
        <rect
          x="-104"
          y="612"
          width="240"
          height="240"
          rx="62"
          transform="rotate(-20 16 732)"
          fill="none"
          stroke="#d9cbb7"
          strokeWidth="2"
          opacity="0.6"
        />
        <rect
          x="-56"
          y="660"
          width="240"
          height="240"
          rx="62"
          transform="rotate(-20 64 780)"
          fill="#e3ede4"
          opacity="0.55"
        />
        <circle cx="352" cy="792" r="180" fill="url(#meaning-bd-glow-sm)" />
      </svg>
    </div>
  );
}
